import React, { useMemo, useState, useCallback, useEffect, useRef } from "react";
import {
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Select,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import PublishIcon from "@material-ui/icons/Publish";
import { Remove } from "@material-ui/icons";
import DialogImageDelete from "./dialogImageDelete";
import DialogSoundDelete from "./dialogSoundDelete";
import DialogVideoDelete from "./dialogVideoDelete";
import DialogFontDelete from "./dialogFontDelete";
import DialogAtlasDelete from "./dialogAtlasDelete";
import DialogBitmapFontDelete from "./dialogBitmapFontDelete";
import { SimpleTreeView, TreeItem } from "@mui/x-tree-view";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import DisabledByDefaultRoundedIcon from "@mui/icons-material/DisabledByDefaultRounded";
import InnerComponentIcon from "../../images/gameConfig/Inner-component-icon.svg";
import { getServerUri } from "../modules/uriUtility";
import { sendCommand } from "../modules/playEngineBridge";
import {
  genAssetKey,
  isPairDepType,
  assetIdFor,
  getAssetFolder,
  getCollectionForType,
  getMediaForBucketAndType,
  buildPairGroups,
  getPairDepConfig,
  buildServerSourceRef,
  mongoIdFromAssetEntry,
  assetKeyFromDependency,
  assetFileToBase64,
  localFileToBase64,
  rewriteAsJsonDataUrl,
  resolvePlayEngineConfig,
  getAssetMapFromConfig,
  normalizeAssetKey,
  validateAssetKey,
  collectAssetKeyUsages,
  rekeyAssetInConfig,
  removeAssetFromPlayEngineConfig,
  collectBucketsByType,
  countBucketsAssets,
  NO_BUCKET,
  BITMAP_FONT_SOURCE_ACCEPT,
  uploadFontAndGenerateBitmapFont,
} from "../modules/playEngineAssetUtils";
import BucketPicker from "./playEngineBucketPicker";
import _ from "lodash";
import PlayEngineTabSearch, { matchesTabSearch } from "./playEngineTabSearch";

const CARD_BG = "#474747";
const CARD_BG_INNER = "#313131";

const ASSET_TYPES = ["image", "audio", "video", "font", "atlas", "bitmap-font"];

function EndIcon(props) {
  return <DisabledByDefaultRoundedIcon {...props} sx={{ opacity: 0.3 }} />;
}

function DropdownRemoveButton({ onClick }) {
  return (
    <div style={{ marginLeft: "auto", flexShrink: 0 }}>
      <IconButton size="small" style={{ padding: 0, color: "#949494" }} onClick={onClick}>
        <Remove fontSize="small" />
      </IconButton>
    </div>
  );
}

function mediaDeleteKindForType(type) {
  switch (type) {
    case "audio": return "sound";
    case "font": return "font";
    case "video": return "video";
    case "atlas": return "atlas";
    case "bitmap-font": return "bitmap-font";
    default: return "image";
  }
}

function assetTypeItemId(type) {
  return `assets-${type}`;
}

function throwIfUploadFailed(resp) {
  if (resp.status === 200) return;
  const err = new Error(`Upload failed (status ${resp.status})`);
  err.status = resp.status;
  throw err;
}

function normalizeBucket(value) {
  const trimmed = (value || "").trim().toLowerCase();
  return trimmed || "nobucket";
}

const MEDIA_THUMB_STYLE = {
  height: "30px",
  maxWidth: "60px",
  objectFit: "contain",
  marginRight: "8px",
};

function MediaSelectOption({ record, folder, label }) {
  if (!record) return null;
  const name = label ?? (record.uploadedFileName || record.fontFamily || record.name || record.file);
  return (
    <div style={{ display: "flex", alignItems: "center", minWidth: 0 }}>
      {record.thumbFile && (
        <img
          src={`${folder}/${record.thumbFile}`}
          alt=""
          style={MEDIA_THUMB_STYLE}
          onError={(e) => { e.target.style.display = "none"; }}
        />
      )}
      <Typography variant="body2" noWrap style={{ maxWidth: "180px" }}>
        {name}
      </Typography>
    </div>
  );
}

function getAcceptString(type) {
  switch (type) {
    case "image": return ".png,.jpg,.jpeg,.gif,.webp";
    case "audio": return ".mp3,.ogg,.wav,.m4a,.aac";
    case "video": return ".mp4,.webm";
    case "font": return ".ttf,.otf,.woff,.woff2";
    case "atlas": return ".json,.png,.jpg,.jpeg";
    case "bitmap-font": return ".xml,.png,.jpg,.jpeg";
    default: return "*";
  }
}

function componentUsageLabel(comp) {
  if (!comp?.id) return null;
  return comp.type ? `${comp.type} - (${comp.id})` : comp.id;
}

/**
 * Assets tab — manages the global asset map at `playEngineConfig.assets`.
 *
 * Each entry is shared: every component dependency (and localization override)
 * bound to its key uses the same playEngine AssetHandle (`${type}:${assetKey}`).
 * Changing an asset's source here therefore updates every component using it.
 */
export default function ContainerPlayEngineAssets(props) {
  const versionId = _.get(props, "selectedVersion._id");
  const config = useMemo(
    () => resolvePlayEngineConfig(props.selectedVersion, props.versions),
    [versionId, props.selectedVersion, props.versions]
  );
  const assetsMap = useMemo(() => getAssetMapFromConfig(config), [config]);

  // Company-wide buckets available per asset type (loaded media + this config),
  // plus a per-type pickable-asset count so empty buckets can be flagged.
  const { bucketsByType, bucketCountsByType } = useMemo(() => {
    const byType = collectBucketsByType(props, config);
    const countsByType = {};
    for (const type of ASSET_TYPES) {
      countsByType[type] = countBucketsAssets(props, [...byType[type], NO_BUCKET], { type });
    }
    return { bucketsByType: byType, bucketCountsByType: countsByType };
  }, [props.media, props.atlases, props.bitmapFonts, props.sounds, props.fonts, props.videos, config]);

  const [expandedItems, setExpandedItems] = useState([]);
  const [search, setSearch] = useState("");
  const [busyKeys, setBusyKeys] = useState({});
  const [addDialog, setAddDialog] = useState(null); // { type, bucket }
  const [addStaged, setAddStaged] = useState({}); // pair upload staging
  const [mediaDeleteDialog, setMediaDeleteDialog] = useState({
    kind: null,
    open: false,
    record: null,
    groupId: null,
    selectedId: null,
  });
  const mediaDeleteRebindRef = useRef(null);

  // ── Derived data ──────────────────────────────────────────────────────────

  // Reference count + component labels for each asset key.
  const { usageByKey, usageLabelsByKey } = useMemo(() => {
    const counts = {};
    const labels = {};
    const addUsage = (assetKey, label) => {
      if (!assetKey || !label) return;
      counts[assetKey] = (counts[assetKey] || 0) + 1;
      if (!labels[assetKey]) labels[assetKey] = [];
      if (!labels[assetKey].includes(label)) labels[assetKey].push(label);
    };

    for (const comp of config.components || []) {
      const label = componentUsageLabel(comp);
      for (const dep of comp.dependencies || []) {
        if (dep?.assetKey) addUsage(dep.assetKey, label);
      }
    }

    const compById = Object.fromEntries((config.components || []).map((c) => [c.id, c]));
    const overrides = _.get(config, "localization.overrides") || {};
    for (const [language, langOverrides] of Object.entries(overrides)) {
      for (const [componentId, compOverride] of Object.entries(langOverrides || {})) {
        const label = componentUsageLabel(compById[componentId]) || componentId;
        for (const v of Object.values(compOverride?.dependencyOverrides || {})) {
          if (typeof v === "string") addUsage(v, `${label} [${language}]`);
        }
      }
    }

    return { usageByKey: counts, usageLabelsByKey: labels };
  }, [config.components, config.localization]);

  const rowsByType = useMemo(() => {
    const grouped = Object.fromEntries(ASSET_TYPES.map((t) => [t, []]));
    Object.entries(assetsMap)
      .map(([assetKey, entry]) => ({ assetKey, ...entry }))
      .sort((a, b) => (a.bucket || "").localeCompare(b.bucket || "") || (a.assetKey || "").localeCompare(b.assetKey || ""))
      .forEach((row) => {
        if (grouped[row.type]) grouped[row.type].push(row);
      });
    return grouped;
  }, [assetsMap]);

  const totalVisibleAssets = useMemo(
    () => ASSET_TYPES.reduce((sum, type) => sum + (rowsByType[type]?.length || 0), 0),
    [rowsByType]
  );
  const searchTypes = ASSET_TYPES.filter((type) =>
    rowsByType[type].some((row) => matchesTabSearch(row.assetKey, search))
  );

  function openAddDialog(type) {
    setAddStaged({});
    setAddDialog({ type, bucket: "nobucket" });
  }

  // Jump to + highlight a row when linked from Version Settings.
  useEffect(() => {
    const key = props.highlightAssetKey;
    if (!key || !assetsMap[key]) return;

    const assetType = assetsMap[key]?.type;
    if (assetType) {
      const itemId = assetTypeItemId(assetType);
      setExpandedItems((prev) => (prev.includes(itemId) ? prev : [...prev, itemId]));
    }

    const scrollTimer = window.setTimeout(() => {
      const row = document.getElementById(`playEngine-asset-row-${key}`);
      const panel = row?.closest('[role="tabpanel"]:not([hidden])');
      if (row && panel) {
        const panelRect = panel.getBoundingClientRect();
        const rowRect = row.getBoundingClientRect();
        panel.scrollTop += rowRect.top - panelRect.top - panel.clientHeight / 2 + row.clientHeight / 2;
      }
    }, 350);

    const clearTimer = window.setTimeout(() => props.onHighlightDone?.(), 1000);

    return () => {
      window.clearTimeout(scrollTimer);
      window.clearTimeout(clearTimer);
    };
  }, [props.highlightAssetKey, assetsMap, props.onHighlightDone]);

  // ── Persistence + iframe helpers ───────────────────────────────────────────

  const persist = useCallback((mutator) => {
    const base = resolvePlayEngineConfig(props.selectedVersion, props.versions);
    const next = _.cloneDeep(base);
    if (!next.assets || Array.isArray(next.assets)) next.assets = {};
    mutator(next);
    props.setSelectedVersionPathValue({ path: "playEngineConfig", value: next });
  }, [props.selectedVersion, props.versions, props.setSelectedVersionPathValue]);

  function setBusy(assetKey, v) {
    setBusyKeys((prev) => ({ ...prev, [assetKey]: v }));
  }

  /** Resolve a chosen media record (or pair group) into { sourceRef, base64 }. */
  async function resolveSelection(type, record, pairGroup) {
    if (isPairDepType(type)) {
      const cfg = getPairDepConfig(type);
      const [imageB64, rawMetaB64] = await Promise.all([
        assetFileToBase64(pairGroup.imageRecord.file, type),
        assetFileToBase64(pairGroup[cfg.metaRecordKey].file, type),
      ]);
      const metaVal = type === "atlas" ? rewriteAsJsonDataUrl(rawMetaB64) : rawMetaB64;
      return {
        sourceRef: buildServerSourceRef(type, String(pairGroup.imageRecord._id), pairGroup),
        base64: { image: imageB64, [cfg.metaKey]: metaVal },
      };
    }
    const base64 = await assetFileToBase64(record.file, type);
    return { sourceRef: buildServerSourceRef(type, String(record._id)), base64 };
  }

  /** Resolve loaded bytes for live preview (iframe handle, then @server media). */
  async function resolveLiveSource(assetKey, type, entry) {
    try {
      const resp = await sendCommand(props.iFrameId, { type: "playengine:get-assets" }, 5000);
      const loaded = resp?.assets?.[assetKey];
      if (loaded?.source) return loaded.source;
    } catch (err) {
      console.warn(`[Assets] get-assets failed, falling back to media: ${err.message}`);
    }

    const mongoId = mongoIdFromAssetEntry(entry);
    if (!mongoId) return null;

    const media = getMediaForBucketAndType(props, entry.bucket || "nobucket", type);
    if (isPairDepType(type)) {
      const cfg = getPairDepConfig(type);
      const groups = buildPairGroups(media, cfg);
      const group =
        groups.find((g) => g[cfg.groupRecordKey] === mongoId) ||
        groups.find((g) => String(g.imageRecord?._id) === mongoId);
      if (group) return (await resolveSelection(type, null, group)).base64;
    } else {
      const record = media.find((m) => String(m._id) === mongoId);
      if (record) return (await resolveSelection(type, record, null)).base64;
    }
    return null;
  }

  /** Rewire iframe bindings after an asset key change. */
  async function rewireAssetUsages(usages, type, newKey, previewTimeout) {
    const newHandleKey = assetIdFor(type, newKey);
    for (const usage of usages) {
      if (usage.kind === "dep") {
        await sendCommand(props.iFrameId, {
          type: "playengine:dispatch-action",
          action: {
            target: usage.componentId,
            name: "changeDependency",
            args: { key: usage.depKey, handleKey: newHandleKey },
          },
        }, previewTimeout);
      } else {
        await sendCommand(props.iFrameId, {
          type: "playengine:dispatch-action",
          action: {
            target: "engine",
            name: "addLocalizationOverrideDependency",
            args: {
              language: usage.language,
              componentId: usage.componentId,
              dependencyKey: usage.depKey,
              handleKey: newHandleKey,
            },
          },
        }, previewTimeout);
      }
    }
  }

  /** Push an asset's bytes to the iframe so every bound handle updates live. */
  async function loadHandle(type, assetKey, base64) {
    try {
      await sendCommand(
        props.iFrameId,
        { type: "playengine:set-asset", assetId: assetIdFor(type, assetKey), source: base64 },
        isPairDepType(type) ? 15000 : 5000
      );
    } catch (err) {
      console.warn(`[Assets] set-asset failed (config saved): ${err.message}`);
    }
  }

  // ── Operations ──────────────────────────────────────────────────────────────

  async function changeAssetSource(assetKey, type, record, pairGroup) {
    setBusy(assetKey, true);
    try {
      const { sourceRef, base64 } = await resolveSelection(type, record, pairGroup);
      persist((next) => {
        const e = next.assets[assetKey];
        if (e) { e.sourceRef = sourceRef; e.source = sourceRef; }
      });
      await loadHandle(type, assetKey, base64);
    } catch (err) {
      console.error("changeAssetSource failed:", err);
      props.createSnackbar?.(`Failed to update asset: ${err.message}`, "error", false);
    } finally {
      setBusy(assetKey, false);
    }
  }

  async function clearAssetSource(assetKey, type) {
    setBusy(assetKey, true);
    try {
      let usages = [];
      persist((next) => {
        const e = next.assets[assetKey];
        if (e) {
          e.sourceRef = null;
          e.source = null;
        }
        usages = collectAssetKeyUsages(next, assetKey);
      });

      const emptyHandle = `${type}:empty`;
      const timeout = isPairDepType(type) ? 15000 : 5000;
      for (const u of usages) {
        try {
          if (u.kind === "override") {
            await sendCommand(props.iFrameId, {
              type: "playengine:dispatch-action",
              action: {
                target: "engine",
                name: "addLocalizationOverrideDependency",
                args: {
                  language: u.language,
                  componentId: u.componentId,
                  dependencyKey: u.depKey,
                  handleKey: emptyHandle,
                },
              },
            }, timeout);
          } else {
            await sendCommand(props.iFrameId, {
              type: "playengine:dispatch-action",
              action: {
                target: u.componentId,
                name: "changeDependency",
                args: { key: u.depKey, handleKey: emptyHandle },
              },
            }, timeout);
          }
        } catch (err) {
          console.warn(`[Assets] clear-source rebind failed for "${u.componentId}": ${err.message}`);
        }
      }
    } catch (err) {
      console.error("clearAssetSource failed:", err);
      props.createSnackbar?.(`Failed to clear asset source: ${err.message}`, "error", false);
    } finally {
      setBusy(assetKey, false);
    }
  }

  async function reassignSourceAfterMediaDelete(ctx) {
    const { assetKey, type, bucket, deletedId, deletedGroupId } = ctx;
    const media = getMediaForBucketAndType(props, bucket, type);
    if (isPairDepType(type)) {
      const cfg = getPairDepConfig(type);
      const groups = buildPairGroups(media, cfg).filter(
        (g) => g[cfg.groupRecordKey] !== deletedGroupId
      );
      if (groups[0]) await changeAssetSource(assetKey, type, null, groups[0]);
      else await clearAssetSource(assetKey, type);
      return;
    }
    const remaining = media.filter((m) => String(m._id) !== String(deletedId));
    if (remaining[0]) await changeAssetSource(assetKey, type, remaining[0], null);
    else await clearAssetSource(assetKey, type);
  }

  function canDeleteAssets() {
    return props.userPermissions?.delete !== false;
  }

  function openDropdownDelete(e, opener) {
    e.preventDefault();
    e.stopPropagation();
    if (!canDeleteAssets()) {
      props.createSnackbar?.(
        "You do not have permission to delete, contact your administrator.",
        "warning",
        false
      );
      return;
    }
    opener();
  }

  function openSourceMediaDelete(kind, record, selectedId, groupId, rebindContext) {
    mediaDeleteRebindRef.current = rebindContext;
    setMediaDeleteDialog({ kind, open: true, record, selectedId, groupId });
  }

  function closeMediaDeleteDialog() {
    mediaDeleteRebindRef.current = null;
    setMediaDeleteDialog({
      kind: null,
      open: false,
      record: null,
      selectedId: null,
      groupId: null,
    });
  }

  async function handleMediaDeletedAfter() {
    const ctx = mediaDeleteRebindRef.current;
    mediaDeleteRebindRef.current = null;
    if (!ctx?.wasSelected) return;
    await reassignSourceAfterMediaDelete(ctx);
  }

  async function rekeyAsset(oldKey, type, entry, newKeyRaw) {
    const newKey = normalizeAssetKey(newKeyRaw);
    if (!newKey || newKey === oldKey) return;

    const err = validateAssetKey(newKey, Object.keys(assetsMap), oldKey);
    if (err) {
      props.createSnackbar?.(err, "warning", false);
      return;
    }

    setBusy(oldKey, true);
    const previewTimeout = isPairDepType(type) ? 15000 : 5000;
    try {
      const baseConfig = resolvePlayEngineConfig(props.selectedVersion, props.versions);
      const usages = collectAssetKeyUsages(baseConfig, oldKey);
      const liveSource = await resolveLiveSource(oldKey, type, entry);

      persist((next) => {
        rekeyAssetInConfig(next, oldKey, newKey);
      });

      if (liveSource) {
        await loadHandle(type, newKey, liveSource);
        if (usages.length > 0) {
          await rewireAssetUsages(usages, type, newKey, previewTimeout);
        }
        try {
          await sendCommand(props.iFrameId, {
            type: "playengine:delete-asset",
            assetKey: oldKey,
            assetType: type,
          }, previewTimeout);
        } catch (err) {
          console.warn(`[Assets] delete-asset after rekey failed: ${err.message}`);
        }
      }

      props.createSnackbar?.(`Asset key updated to "${newKey}".`, "success", false);
      if (props.highlightAssetKey === oldKey) {
        props.onNavigateToAsset?.(newKey);
      }
    } catch (err) {
      console.error("rekeyAsset failed:", err);
      props.createSnackbar?.(`Failed to rename key: ${err.message}`, "error", false);
    } finally {
      setBusy(oldKey, false);
      setBusy(newKey, false);
    }
  }

  /**
   * Re-bucket an asset-map key. Besides changing the key's bucket, this:
   *   - moves every component bound to this key into the new bucket, and
   *   - re-points the key's source to the first available asset of the new
   *     bucket (empty if none).
   * Every component sharing the key updates live (the handle bytes change), and
   * each affected component's engine bucket is updated too.
   */
  async function rebucketAsset(assetKey, bucketRaw) {
    const newBucket = normalizeBucket(bucketRaw);
    const entry = assetsMap[assetKey];
    if (!entry) return;
    const type = entry.type;
    if (normalizeBucket(entry.bucket) === newBucket) return; // no change

    setBusy(assetKey, true);
    try {
      // 1. First available asset of the new bucket for this type (or empty).
      let sourceRef = null;
      let base64 = null;
      if (isPairDepType(type)) {
        const cfg = getPairDepConfig(type);
        const group = buildPairGroups(getMediaForBucketAndType(props, newBucket, type), cfg)[0] || null;
        if (group) ({ sourceRef, base64 } = await resolveSelection(type, null, group));
      } else {
        const record = getMediaForBucketAndType(props, newBucket, type)[0] || null;
        if (record) ({ sourceRef, base64 } = await resolveSelection(type, record, null));
      }

      // 2. Persist: key bucket+source, and move bound components to the bucket.
      let usages = [];
      let affectedComponentIds = [];
      persist((next) => {
        const e = next.assets[assetKey];
        if (e) {
          e.bucket = newBucket;
          e.sourceRef = sourceRef;
          e.source = sourceRef;
        }
        usages = collectAssetKeyUsages(next, assetKey);
        const ids = new Set(usages.filter((u) => u.componentId).map((u) => u.componentId));
        affectedComponentIds = Array.from(ids);
        for (const comp of next.components || []) {
          if (ids.has(comp.id)) comp.bucket = newBucket;
        }
      });

      // 3. Live: move each affected component's bucket.
      for (const cid of affectedComponentIds) {
        try {
          await sendCommand(props.iFrameId, { type: "playengine:set-bucket", componentId: cid, bucket: newBucket });
        } catch (err) {
          console.warn(`[Assets] set-bucket failed for "${cid}" (config saved): ${err.message}`);
        }
      }

      // 4. Live: update the shared handle bytes, or empty every bound usage.
      if (base64) {
        await loadHandle(type, assetKey, base64);
      } else {
        const emptyHandle = `${type}:empty`;
        const timeout = isPairDepType(type) ? 15000 : 5000;
        for (const u of usages) {
          try {
            if (u.kind === "override") {
              await sendCommand(props.iFrameId, {
                type: "playengine:dispatch-action",
                action: {
                  target: "engine",
                  name: "addLocalizationOverrideDependency",
                  args: { language: u.language, componentId: u.componentId, dependencyKey: u.depKey, handleKey: emptyHandle },
                },
              }, timeout);
            } else {
              await sendCommand(props.iFrameId, {
                type: "playengine:dispatch-action",
                action: { target: u.componentId, name: "changeDependency", args: { key: u.depKey, handleKey: emptyHandle } },
              }, timeout);
            }
          } catch (err) {
            console.warn(`[Assets] empty-rebind failed for "${u.componentId}" (config saved): ${err.message}`);
          }
        }
      }
    } catch (err) {
      console.error("rebucketAsset failed:", err);
      props.createSnackbar?.(`Failed to re-bucket asset: ${err.message}`, "error", false);
    } finally {
      setBusy(assetKey, false);
    }
  }

  async function deleteAsset(assetKey, type) {
    const used = usageByKey[assetKey] || 0;
    if (used > 0) {
      const ok = window.confirm(
        `This asset is used by ${used} component dependency/dependencies. ` +
        `Deleting it will unbind them. Continue?`
      );
      if (!ok) return;
    }
    setBusy(assetKey, true);
    try {
      persist((next) => {
        removeAssetFromPlayEngineConfig(next, assetKey);
      });
      try {
        await sendCommand(props.iFrameId, {
          type: "playengine:delete-asset",
          assetKey,
          assetType: type,
        }, isPairDepType(type) ? 15000 : 5000);
      } catch (err) {
        console.warn(`[Assets] delete-asset failed: ${err.message}`);
      }
    } finally {
      setBusy(assetKey, false);
    }
  }

  async function addAssetFromSelection(type, bucket, record, pairGroup) {
    const assetKey = genAssetKey(type, Object.keys(assetsMap));
    try {
      const { sourceRef, base64 } = await resolveSelection(type, record, pairGroup);
      persist((next) => {
        next.assets[assetKey] = {
          type,
          bucket: bucket || "nobucket",
          sourceRef,
          source: sourceRef,
        };
      });
      await loadHandle(type, assetKey, base64);
      props.createSnackbar?.(`Added ${type} asset.`, "success", false);
    } catch (err) {
      console.error("addAssetFromSelection failed:", err);
      props.createSnackbar?.(`Failed to add asset: ${err.message}`, "error", false);
    }
  }

  // ── Upload (new media) flows for the Add dialog ─────────────────────────────

  function insertRecordsForType(type, records) {
    switch (type) {
      case "image": return props.insertMediaPathValue?.({ path: "images", value: records });
      case "atlas": return props.insertAtlasPathValue?.(records);
      case "bitmap-font": return props.insertBitmapFontPathValue?.(records);
      case "audio": return props.insertSoundPathValue?.(records);
      case "font": return props.insertFontPathValue?.(records);
      case "video": return props.insertVideoPathValue?.(records);
      default: return undefined;
    }
  }

  function uploadEndpointForType(type) {
    switch (type) {
      case "image": return "media/uploadImage";
      case "audio": return "sound/uploadSound";
      case "font": return "font/uploadFont";
      case "video": return "video/uploadVideo";
      default: return null;
    }
  }

  /**
   * Upload files into the media library and resolve them to an asset source.
   *
   * Every upload endpoint stamps the record with the requester's ACTIVE company
   * (req.user.companyIDString), so a user switched into another company uploads
   * into that company — nothing here sends a company id. `bucket` is the bucket
   * the record is filed under.
   *
   *   files: { file } for simple types, { imageFile, metaFile } for atlas,
   *          { fontFile } for bitmap-font (generated from the font).
   */
  async function uploadToLibrary(type, bucket, files) {
    const effectiveBucket = normalizeBucket(bucket);

    if (type === "bitmap-font") {
      const result = await uploadFontAndGenerateBitmapFont(files.fontFile, {
        bucket: effectiveBucket,
        onFontUploaded: (fontRecords) => props.insertFontPathValue?.(fontRecords),
      });
      insertRecordsForType("bitmap-font", result.records);
      return {
        sourceRef: buildServerSourceRef("bitmap-font", String(result.pairGroup.imageRecord._id), result.pairGroup),
        base64: result.preview,
        warnings: result.warnings,
      };
    }

    if (isPairDepType(type)) {
      const cfg = getPairDepConfig(type);
      const formData = new FormData();
      formData.append("variant", "image");
      formData.append("files", files.imageFile);
      formData.append("variant", cfg.uploadMetaVariant);
      formData.append("files", files.metaFile);
      formData.append("componentType", null);
      formData.append("bucket", effectiveBucket);
      formData.append("accessToken", localStorage.getItem("session.accessToken"));
      formData.append("uploadedFileName", files.imageFile.name.replace(/\.[^/.]+$/, ""));
      const resp = await fetch(getServerUri(cfg.uploadEndpoint), { method: "POST", body: formData });
      throwIfUploadFailed(resp);
      const result = await resp.json();
      const newRecords = _.get(result, "records", []);
      if (newRecords.length < 2) throw new Error(`Expected 2 ${type} records`);
      insertRecordsForType(type, newRecords);

      const imageRecord = newRecords.find((r) => (r.variant || "").toLowerCase() === "image");
      const metaRecord = newRecords.find((r) => (r.variant || "").toLowerCase() === cfg.metaVariant);
      const pairGroup = {
        [cfg.groupRecordKey]: _.get(result, cfg.uploadGroupIdKey),
        imageRecord,
        [cfg.metaRecordKey]: metaRecord,
      };
      const [imageB64, rawMetaB64] = await Promise.all([
        localFileToBase64(files.imageFile),
        localFileToBase64(files.metaFile),
      ]);
      const metaVal = type === "atlas" ? rewriteAsJsonDataUrl(rawMetaB64) : rawMetaB64;
      return {
        sourceRef: buildServerSourceRef(type, String(imageRecord._id), pairGroup),
        base64: { image: imageB64, [cfg.metaKey]: metaVal },
        warnings: [],
      };
    }

    const endpoint = uploadEndpointForType(type);
    if (!endpoint) throw new Error(`No upload endpoint for ${type}`);
    const file = files.file;
    const formData = new FormData();
    formData.append("files", file);
    formData.append("componentType", null);
    formData.append("bucket", effectiveBucket);
    formData.append("accessToken", localStorage.getItem("session.accessToken"));
    formData.append("uploadedFileName", file.name.replace(/\.[^/.]+$/, ""));
    if (type === "font") {
      formData.append("fontFamily", file.name.replace(/\.[^/.]+$/, ""));
    }
    const resp = await fetch(getServerUri(endpoint), { method: "POST", body: formData });
    throwIfUploadFailed(resp);
    const result = await resp.json();
    const newRecords = _.get(result, "records", []);
    if (!newRecords.length) throw new Error("Upload returned no records");
    insertRecordsForType(type, newRecords);
    return {
      sourceRef: buildServerSourceRef(type, String(newRecords[0]._id)),
      base64: await localFileToBase64(file),
      warnings: [],
    };
  }

  /** Add dialog: upload, then bind the result to a NEW asset key. */
  async function uploadAndAdd(type, bucket, files) {
    const { sourceRef, base64, warnings } = await uploadToLibrary(type, bucket, files);
    const assetKey = genAssetKey(type, Object.keys(assetsMap));
    persist((next) => {
      next.assets[assetKey] = {
        type,
        bucket: normalizeBucket(bucket),
        sourceRef,
        source: sourceRef,
      };
    });
    await loadHandle(type, assetKey, base64);
    if (!reportUploadWarnings(warnings) && type === "bitmap-font") {
      props.createSnackbar?.("Bitmap font generated from font.", "success", false);
    }
  }

  /**
   * Asset row: upload a file and make it this key's source. The key is shared,
   * so every component bound to it picks up the new file. The upload is filed
   * under the key's current bucket.
   */
  async function replaceAssetFromUpload(assetKey, type, files) {
    if (props.userPermissions?.update === false) {
      props.createSnackbar?.("You do not have permission to update, contact your administrator.", "warning", false);
      return;
    }
    const entry = assetsMap[assetKey];
    if (!entry) return;

    setBusy(assetKey, true);
    try {
      const { sourceRef, base64, warnings } = await uploadToLibrary(type, entry.bucket, files);
      let stillExists = false;
      persist((next) => {
        const e = next.assets[assetKey];
        if (e) {
          stillExists = true;
          e.sourceRef = sourceRef;
          e.source = sourceRef;
        }
      });
      if (!stillExists) return;
      await loadHandle(type, assetKey, base64);
      if (!reportUploadWarnings(warnings)) {
        props.createSnackbar?.(`Replaced ${type} for "${assetKey}".`, "success", false);
      }
    } catch (err) {
      console.error("replaceAssetFromUpload failed:", err);
      if (err.status === 401) {
        props.createSnackbar?.("You are not authorized to upload.", "warning", false);
      } else {
        props.createSnackbar?.(`Upload failed: ${err.message}`, "error", false);
      }
    } finally {
      setBusy(assetKey, false);
    }
  }

  function reportUploadWarnings(warnings) {
    if (!warnings || !warnings.length) return false;
    props.createSnackbar?.(warnings.join(" "), "warning", false);
    return true;
  }

  // ── Render ───────────────────────────────────────────────────────────────

  const previewReady = props.isPreviewLoaded;

  return (
    <div style={{ padding: "8px 4px" }}>
      <PlayEngineTabSearch value={search} onChange={setSearch} />
      {!previewReady && (
        <Typography variant="body2" style={{ color: "#caa14a", marginBottom: 8 }}>
          Waiting for the preview to load…
        </Typography>
      )}

      {totalVisibleAssets === 0 && !search.trim() && (
        <Typography variant="body2" style={{ color: "#888", marginBottom: 8 }}>
          No assets yet. Expand a type below to add one, or pick media on a component in Version Settings.
        </Typography>
      )}
      {search.trim() && searchTypes.length === 0 && (
        <Typography variant="body2" style={{ color: "#888", padding: "8px" }}>
          No asset keys match this search.
        </Typography>
      )}

      <SimpleTreeView
        aria-label="playEngine-assets"
        expandedItems={search.trim() ? Array.from(new Set([...expandedItems, ...searchTypes.map(assetTypeItemId)])) : expandedItems}
        onExpandedItemsChange={(event, itemIds) => setExpandedItems(itemIds)}
        slots={{
          expandIcon: ArrowRightIcon,
          collapseIcon: ArrowDropDownIcon,
          endIcon: EndIcon,
        }}
        sx={{ overflowX: "hidden", flexGrow: 1 }}
      >
        {ASSET_TYPES.map((type) => {
          const typeRows = (rowsByType[type] || []).filter((row) => matchesTabSearch(row.assetKey, search));
          if (search.trim() && typeRows.length === 0) return null;
          return (
            <Card key={type} style={{ margin: "5px", backgroundColor: CARD_BG }}>
              <TreeItem
                itemId={assetTypeItemId(type)}
                label={(
                  <div style={{ display: "flex", alignItems: "center", minHeight: "32px", fontWeight: "bold" }}>
                    <img
                      src={InnerComponentIcon}
                      alt=""
                      style={{ width: "18px", height: "18px", marginRight: "8px" }}
                    />
                    <span style={{ color: "#eee", textTransform: "capitalize" }}>{type}</span>
                    <Chip
                      label={typeRows.length}
                      size="small"
                      style={{ marginLeft: 8, backgroundColor: CARD_BG_INNER, color: "#aaa", height: 20 }}
                    />
                  </div>
                )}
              >
                <Card style={{ backgroundColor: CARD_BG_INNER, boxShadow: "none", margin: "0 4px 4px" }}>
                  <CardContent style={{ paddingTop: 8, paddingBottom: 12 }}>
                    {typeRows.length === 0 ? (
                      <Typography variant="body2" style={{ color: "#777", marginBottom: 8 }}>
                        No {type} assets yet.
                      </Typography>
                    ) : (
                      typeRows.map((row) => (
                        <AssetRow
                          key={row.assetKey}
                          row={row}
                          usage={usageByKey[row.assetKey] || 0}
                          usageLabels={usageLabelsByKey[row.assetKey] || []}
                          busy={!!busyKeys[row.assetKey]}
                          highlighted={props.highlightAssetKey === row.assetKey}
                          collections={props}
                          buckets={bucketsByType[type] || []}
                          bucketCounts={bucketCountsByType[type] || {}}
                          existingKeys={Object.keys(assetsMap)}
                          onChangeSource={changeAssetSource}
                          onReplaceFromUpload={replaceAssetFromUpload}
                          onRekey={rekeyAsset}
                          onRebucket={rebucketAsset}
                          onDelete={deleteAsset}
                          onDeleteSource={openSourceMediaDelete}
                          openDropdownDelete={openDropdownDelete}
                        />
                      ))
                    )}
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddIcon />}
                      disabled={!previewReady}
                      onClick={() => openAddDialog(type)}
                      style={{ marginTop: 4, color: "#ccc", borderColor: "#666" }}
                    >
                      Add {type} asset
                    </Button>
                  </CardContent>
                </Card>
              </TreeItem>
            </Card>
          );
        })}
      </SimpleTreeView>

      {addDialog && (
        <AddAssetDialog
          state={addDialog}
          setState={setAddDialog}
          staged={addStaged}
          setStaged={setAddStaged}
          collections={props}
          buckets={bucketsByType[addDialog.type] || []}
          bucketCounts={bucketCountsByType[addDialog.type] || {}}
          onSelectExisting={addAssetFromSelection}
          onUpload={uploadAndAdd}
          createSnackbar={props.createSnackbar}
          onClose={() => { setAddDialog(null); setAddStaged({}); }}
        />
      )}

      <DialogImageDelete
        isOpen={mediaDeleteDialog.open && mediaDeleteDialog.kind === "image"}
        setIsOpen={(value) => { if (!value) closeMediaDeleteDialog(); }}
        createSnackbar={props.createSnackbar}
        imageToDelete={mediaDeleteDialog.record}
        selectedImageID={mediaDeleteDialog.selectedId}
        images={_.get(props, "media.images", [])}
        setImages={() => {}}
        selectImage={() => {}}
        deleteImageFromMedia={props.deleteImageFromMedia}
        onAfterDelete={handleMediaDeletedAfter}
      />
      <DialogSoundDelete
        isOpen={mediaDeleteDialog.open && mediaDeleteDialog.kind === "sound"}
        setIsOpen={(value) => { if (!value) closeMediaDeleteDialog(); }}
        createSnackbar={props.createSnackbar}
        soundToDelete={mediaDeleteDialog.record}
        selectedSoundID={mediaDeleteDialog.selectedId}
        sounds={props.sounds || []}
        setSounds={() => {}}
        selectSound={() => {}}
        deleteSoundFromSounds={props.deleteSoundFromSounds}
        onAfterDelete={handleMediaDeletedAfter}
      />
      <DialogVideoDelete
        isOpen={mediaDeleteDialog.open && mediaDeleteDialog.kind === "video"}
        setIsOpen={(value) => { if (!value) closeMediaDeleteDialog(); }}
        createSnackbar={props.createSnackbar}
        videoToDelete={mediaDeleteDialog.record}
        selectedVideoID={mediaDeleteDialog.selectedId}
        videos={props.videos || []}
        setVideos={() => {}}
        selectVideo={() => {}}
        deleteVideoFromVideos={props.deleteVideoFromVideos}
        onAfterDelete={handleMediaDeletedAfter}
      />
      <DialogFontDelete
        isOpen={mediaDeleteDialog.open && mediaDeleteDialog.kind === "font"}
        setIsOpen={(value) => { if (!value) closeMediaDeleteDialog(); }}
        createSnackbar={props.createSnackbar}
        fontToDelete={mediaDeleteDialog.record}
        selectedFontID={mediaDeleteDialog.selectedId}
        fonts={props.fonts || []}
        setFonts={() => {}}
        selectFont={() => {}}
        deleteFontFromFonts={props.deleteFontFromFonts}
        onAfterDelete={handleMediaDeletedAfter}
      />
      <DialogAtlasDelete
        isOpen={mediaDeleteDialog.open && mediaDeleteDialog.kind === "atlas"}
        setIsOpen={(value) => { if (!value) closeMediaDeleteDialog(); }}
        createSnackbar={props.createSnackbar}
        animationSetToDeleteID={mediaDeleteDialog.groupId}
        atlases={props.atlases || []}
        setAtlases={() => {}}
        deleteAnimationSetFromAtlases={props.deleteAnimationSetFromAtlases}
        onAfterDelete={handleMediaDeletedAfter}
      />
      <DialogBitmapFontDelete
        isOpen={mediaDeleteDialog.open && mediaDeleteDialog.kind === "bitmap-font"}
        setIsOpen={(value) => { if (!value) closeMediaDeleteDialog(); }}
        createSnackbar={props.createSnackbar}
        fontSetToDeleteID={mediaDeleteDialog.groupId}
        bitmapFonts={props.bitmapFonts || []}
        setBitmapFonts={() => {}}
        deleteFontSetFromBitmapFonts={props.deleteFontSetFromBitmapFonts}
        onAfterDelete={handleMediaDeletedAfter}
      />
    </div>
  );
}

/** A single editable asset row. */
function AssetRow({
  row,
  usage,
  usageLabels,
  busy,
  highlighted,
  collections,
  buckets,
  bucketCounts,
  existingKeys,
  onChangeSource,
  onReplaceFromUpload,
  onRekey,
  onRebucket,
  onDelete,
  onDeleteSource,
  openDropdownDelete,
}) {
  const { assetKey, type, bucket } = row;
  const [keyDraft, setKeyDraft] = useState(assetKey);
  const [keyError, setKeyError] = useState("");
  const [stagedPair, setStagedPair] = useState({ imageFile: null, metaFile: null });

  useEffect(() => {
    setKeyDraft(assetKey);
    setKeyError("");
  }, [assetKey]);

  const effectiveBucket = normalizeBucket(bucket);

  const currentMongoId = mongoIdFromAssetEntry(row);

  const pairConfig = isPairDepType(type) ? getPairDepConfig(type) : null;
  const media = getMediaForBucketAndType(collections, effectiveBucket, type);
  const pairGroups = pairConfig ? buildPairGroups(media, pairConfig) : [];
  const allTypeMedia = getCollectionForType(collections, type);
  const allPairGroups = pairConfig ? buildPairGroups(allTypeMedia, pairConfig) : [];
  const currentPair = pairConfig
    ? pairGroups.find((g) => String(g.imageRecord._id) === currentMongoId)
      || allPairGroups.find((g) => String(g.imageRecord._id) === currentMongoId)
    : null;
  const currentPairId = currentPair?.[pairConfig?.groupRecordKey] ?? "";
  const selectValue = pairConfig
    ? currentPairId
    : (media.some((m) => String(m._id) === currentMongoId) ? currentMongoId : "");

  function renderSelectedSource(value) {
    if (!value) return <em style={{ color: "#888" }}>— select media —</em>;
    if (pairConfig) {
      const group = pairGroups.find((g) => g[pairConfig.groupRecordKey] === value)
        || allPairGroups.find((g) => g[pairConfig.groupRecordKey] === value);
      if (group) {
        return (
          <MediaSelectOption
            record={group.imageRecord}
            folder={pairConfig.staticFolder}
          />
        );
      }
    } else {
      const record = media.find((m) => String(m._id) === value)
        || allTypeMedia.find((m) => String(m._id) === value);
      if (record) {
        return <MediaSelectOption record={record} folder={getAssetFolder(type)} />;
      }
    }
    return value;
  }

  function handleSourceChange(value) {
    if (pairConfig) {
      const g = pairGroups.find((x) => x[pairConfig.groupRecordKey] === value);
      if (g) onChangeSource(assetKey, type, null, g);
    } else {
      const rec = media.find((m) => String(m._id) === value);
      if (rec) onChangeSource(assetKey, type, rec, null);
    }
  }

  function commitKeyChange() {
    const next = normalizeAssetKey(keyDraft);
    setKeyDraft(next);
    if (next === assetKey) {
      setKeyError("");
      return;
    }
    const err = validateAssetKey(next, existingKeys, assetKey);
    if (err) {
      setKeyError(err);
      return;
    }
    setKeyError("");
    onRekey(assetKey, type, row, next);
  }

  return (
    <Card
      id={`playEngine-asset-row-${assetKey}`}
      style={{
        backgroundColor: highlighted ? "#3d4a5c" : CARD_BG,
        marginBottom: 8,
        outline: highlighted ? "2px solid #7e9bd6" : "2px solid transparent",
        boxShadow: highlighted ? "0 0 16px rgba(126, 155, 214, 0.45)" : "none",
        transition: "background-color 0.35s ease, box-shadow 0.35s ease, outline-color 0.35s ease",
      }}
    >
      <CardContent style={{ paddingBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 4 }}>
          <Tooltip
            title={
              usageLabels.length > 0 ? (
                <div>
                  {usageLabels.map((label) => (
                    <div key={label}>{label}</div>
                  ))}
                </div>
              ) : "Not used by any component"
            }
          >
            <Chip
              label={`${usage} use${usage === 1 ? "" : "s"}`}
              size="small"
              style={{ backgroundColor: CARD_BG_INNER, color: usage ? "#cfcf8a" : "#777", marginRight: 4 }}
            />
          </Tooltip>
          <div style={{ flex: 1 }} />
          {busy && <CircularProgress size={18} />}
          <Tooltip title="Delete asset">
            <span>
              <IconButton size="small" disabled={busy} onClick={() => onDelete(assetKey, type)}>
                <DeleteIcon fontSize="small" style={{ color: "#d98a8a" }} />
              </IconButton>
            </span>
          </Tooltip>
        </div>

        <BucketPicker
          bucket={bucket}
          buckets={buckets}
          bucketCounts={bucketCounts}
          disabled={busy}
          onBucketChange={(val) => onRebucket(assetKey, val)}
          style={{ marginTop: 10 }}
        />

        <div style={{ display: "flex", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
          <TextField
            label="Key"
            value={keyDraft}
            disabled={busy}
            error={!!keyError}
            helperText={keyError || undefined}
            onChange={(e) => {
              setKeyDraft(e.target.value);
              if (keyError) setKeyError("");
            }}
            onBlur={commitKeyChange}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                commitKeyChange();
              }
            }}
            InputProps={{ style: { color: "#eee", fontFamily: "monospace" } }}
            InputLabelProps={{ style: { color: "#999" } }}
            style={{ minWidth: 180 }}
          />

          <div style={{ flex: 1, minWidth: 200 }}>
            <Typography variant="caption" style={{ color: "#999", display: "block" }}>Source</Typography>
            <Select
              value={selectValue}
              displayEmpty
              disabled={busy}
              renderValue={renderSelectedSource}
              onChange={(e) => handleSourceChange(e.target.value)}
              style={{ color: "#eee", width: "100%" }}
            >
              <MenuItem value=""><em style={{ color: "#888" }}>— select media —</em></MenuItem>
              {pairConfig
                ? pairGroups.map((g) => {
                    const id = g[pairConfig.groupRecordKey];
                    return (
                      <MenuItem
                        key={id}
                        value={id}
                        style={{ display: "flex", justifyContent: "space-between", width: "100%" }}
                      >
                        <MediaSelectOption
                          record={g.imageRecord}
                          folder={pairConfig.staticFolder}
                        />
                        <DropdownRemoveButton
                          onClick={(e) => openDropdownDelete(e, () => onDeleteSource(
                            mediaDeleteKindForType(type),
                            g.imageRecord,
                            selectValue,
                            id,
                            {
                              assetKey,
                              type,
                              bucket: effectiveBucket,
                              deletedId: g.imageRecord._id,
                              deletedGroupId: id,
                              wasSelected: selectValue === id,
                            }
                          ))}
                        />
                      </MenuItem>
                    );
                  })
                : media.map((m) => (
                    <MenuItem
                      key={String(m._id)}
                      value={String(m._id)}
                      style={{ display: "flex", justifyContent: "space-between", width: "100%" }}
                    >
                      <MediaSelectOption record={m} folder={getAssetFolder(type)} />
                      <DropdownRemoveButton
                        onClick={(e) => openDropdownDelete(e, () => onDeleteSource(
                          mediaDeleteKindForType(type),
                          m,
                          selectValue,
                          null,
                          {
                            assetKey,
                            type,
                            bucket: effectiveBucket,
                            deletedId: m._id,
                            deletedGroupId: null,
                            wasSelected: String(selectValue) === String(m._id),
                          }
                        ))}
                      />
                    </MenuItem>
                  ))}
            </Select>
          </div>
        </div>

        <AssetRowUpload
          assetKey={assetKey}
          type={type}
          bucket={effectiveBucket}
          busy={busy}
          stagedPair={stagedPair}
          setStagedPair={setStagedPair}
          onUpload={(files) => onReplaceFromUpload(assetKey, type, files)}
        />
      </CardContent>
    </Card>
  );
}

const ROW_UPLOAD_BUTTON_STYLE = { color: "#ccc", borderColor: "#666", textTransform: "none" };
const ROW_UPLOAD_STAGED_STYLE = { color: "#8fd18f", borderColor: "#5f9a5f" };

/**
 * Per-row upload: replaces the key's source with a file from the user's
 * computer, filed under the row's bucket. Atlas needs both halves first; a
 * bitmap font is generated from a single font file.
 */
function AssetRowUpload({ assetKey, type, bucket, busy, stagedPair, setStagedPair, onUpload }) {
  const inputId = `playEngine-asset-upload-${assetKey}`;
  const hint = `Uploads into bucket “${bucket}” and replaces this key for every component using it`;

  function pickAndUpload(fileKey, accept, label) {
    return (
      <>
        <input
          id={inputId}
          type="file"
          hidden
          accept={accept}
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = "";
            if (file) onUpload({ [fileKey]: file });
          }}
        />
        <Tooltip title={hint}>
          <span>
            <Button
              size="small"
              variant="outlined"
              startIcon={<PublishIcon fontSize="small" />}
              disabled={busy}
              onClick={() => document.getElementById(inputId)?.click()}
              style={ROW_UPLOAD_BUTTON_STYLE}
            >
              {label}
            </Button>
          </span>
        </Tooltip>
      </>
    );
  }

  let controls;
  if (type === "bitmap-font") {
    controls = pickAndUpload("fontFile", BITMAP_FONT_SOURCE_ACCEPT, "Upload font");
  } else if (type === "atlas") {
    const pairConfig = getPairDepConfig(type);
    const ready = !!(stagedPair.imageFile && stagedPair.metaFile);
    controls = (
      <>
        <input
          id={`${inputId}-img`}
          type="file"
          hidden
          accept=".png,.jpg,.jpeg"
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = "";
            if (file) setStagedPair({ ...stagedPair, imageFile: file });
          }}
        />
        <input
          id={`${inputId}-meta`}
          type="file"
          hidden
          accept={pairConfig.metaAccept}
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = "";
            if (file) setStagedPair({ ...stagedPair, metaFile: file });
          }}
        />
        <Button
          size="small"
          variant="outlined"
          disabled={busy}
          onClick={() => document.getElementById(`${inputId}-img`)?.click()}
          style={{ ...ROW_UPLOAD_BUTTON_STYLE, ...(stagedPair.imageFile ? ROW_UPLOAD_STAGED_STYLE : null) }}
        >
          {stagedPair.imageFile ? "✓ IMG" : "+ IMG"}
        </Button>
        <Button
          size="small"
          variant="outlined"
          disabled={busy}
          onClick={() => document.getElementById(`${inputId}-meta`)?.click()}
          style={{ ...ROW_UPLOAD_BUTTON_STYLE, ...(stagedPair.metaFile ? ROW_UPLOAD_STAGED_STYLE : null) }}
        >
          {stagedPair.metaFile ? `✓ ${pairConfig.metaLabel}` : `+ ${pairConfig.metaLabel}`}
        </Button>
        <Tooltip title={ready ? hint : `Pick both the image and the ${pairConfig.metaLabel} first`}>
          <span>
            <Button
              size="small"
              variant="outlined"
              startIcon={<PublishIcon fontSize="small" />}
              disabled={busy || !ready}
              onClick={() => {
                onUpload({ imageFile: stagedPair.imageFile, metaFile: stagedPair.metaFile });
                setStagedPair({ imageFile: null, metaFile: null });
              }}
              style={ROW_UPLOAD_BUTTON_STYLE}
            >
              Upload
            </Button>
          </span>
        </Tooltip>
      </>
    );
  } else if (uploadableSimple(type)) {
    controls = pickAndUpload("file", getAcceptString(type), "Upload");
  } else {
    return null;
  }

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
      {controls}
    </div>
  );
}

/** Add-asset dialog: pick bucket, then select existing media or upload. */
function AddAssetDialog({
  state,
  setState,
  staged,
  setStaged,
  collections,
  buckets,
  bucketCounts,
  onSelectExisting,
  onUpload,
  createSnackbar,
  onClose,
}) {
  const { type, bucket } = state;
  const [submitting, setSubmitting] = useState(false);
  const effectiveBucket = normalizeBucket(bucket);
  const isAtlasPairUpload = type === "atlas";
  const isBitmapFontFromFont = type === "bitmap-font";
  const pairConfig = isPairDepType(type) ? getPairDepConfig(type) : null;
  const media = getMediaForBucketAndType(collections, effectiveBucket, type);
  const pairGroups = pairConfig ? buildPairGroups(media, pairConfig) : [];

  async function handleSelectExisting(value) {
    setSubmitting(true);
    try {
      if (pairConfig) {
        const g = pairGroups.find((x) => x[pairConfig.groupRecordKey] === value);
        if (g) await onSelectExisting(type, effectiveBucket, null, g);
      } else {
        const rec = media.find((m) => String(m._id) === value);
        if (rec) await onSelectExisting(type, effectiveBucket, rec, null);
      }
      onClose();
    } finally {
      setSubmitting(false);
    }
  }

  async function handleUpload() {
    setSubmitting(true);
    try {
      if (isBitmapFontFromFont) {
        if (!staged.fontFile) return;
        await onUpload(type, effectiveBucket, { fontFile: staged.fontFile });
      } else if (isAtlasPairUpload) {
        if (!staged.imageFile || !staged.metaFile) return;
        await onUpload(type, effectiveBucket, { imageFile: staged.imageFile, metaFile: staged.metaFile });
      } else {
        if (!staged.file) return;
        await onUpload(type, effectiveBucket, { file: staged.file });
      }
      onClose();
    } catch (err) {
      console.error("Add asset upload failed:", err);
      if (err.status === 401) {
        createSnackbar?.("You are not authorized to upload.", "warning", false);
      } else {
        createSnackbar?.(`Upload failed: ${err.message}`, "error", false);
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add {type} asset</DialogTitle>
      <DialogContent>
        <BucketPicker
          bucket={bucket}
          buckets={buckets}
          bucketCounts={bucketCounts}
          disabled={submitting}
          onBucketChange={(val) => setState({ ...state, bucket: val })}
          style={{ marginBottom: 8 }}
        />

        <Typography variant="subtitle2" style={{ marginBottom: 6 }}>Select existing media</Typography>
        <Select
          value=""
          displayEmpty
          fullWidth
          disabled={submitting}
          onChange={(e) => handleSelectExisting(e.target.value)}
        >
          <MenuItem value="" disabled><em>— choose from “{effectiveBucket}” —</em></MenuItem>
          {pairConfig
            ? pairGroups.map((g) => {
                const id = g[pairConfig.groupRecordKey];
                return (
                  <MenuItem key={id} value={id}>
                    <MediaSelectOption
                      record={g.imageRecord}
                      folder={pairConfig.staticFolder}
                    />
                  </MenuItem>
                );
              })
            : media.map((m) => (
                <MenuItem key={String(m._id)} value={String(m._id)}>
                  <MediaSelectOption record={m} folder={getAssetFolder(type)} />
                </MenuItem>
              ))}
        </Select>

        <Typography variant="subtitle2" style={{ margin: "16px 0 6px" }}>Or upload new</Typography>
        {isBitmapFontFromFont ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <Typography variant="caption" style={{ color: "#aaa" }}>
              Upload a font (TTF/OTF/WOFF) — a bitmap font (PNG + XML) is generated automatically.
            </Typography>
            <Button variant="outlined" component="label" disabled={submitting}>
              {staged.fontFile ? staged.fontFile.name : "Choose font"}
              <input
                type="file"
                hidden
                accept={BITMAP_FONT_SOURCE_ACCEPT}
                onChange={(e) => setStaged({ ...staged, fontFile: e.target.files?.[0] })}
              />
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={submitting || !staged.fontFile}
              onClick={handleUpload}
            >
              Generate &amp; add
            </Button>
          </div>
        ) : isAtlasPairUpload ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <Button variant="outlined" component="label" disabled={submitting}>
              {staged.imageFile ? `Image: ${staged.imageFile.name}` : "Choose image"}
              <input type="file" hidden accept=".png,.jpg,.jpeg" onChange={(e) => setStaged({ ...staged, imageFile: e.target.files?.[0] })} />
            </Button>
            <Button variant="outlined" component="label" disabled={submitting}>
              {staged.metaFile ? `${pairConfig.metaLabel}: ${staged.metaFile.name}` : `Choose ${pairConfig.metaLabel}`}
              <input type="file" hidden accept={pairConfig.metaAccept} onChange={(e) => setStaged({ ...staged, metaFile: e.target.files?.[0] })} />
            </Button>
            <Button variant="contained" color="primary" disabled={submitting || !staged.imageFile || !staged.metaFile} onClick={handleUpload}>
              Upload &amp; add
            </Button>
          </div>
        ) : uploadableSimple(type) ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <Button variant="outlined" component="label" disabled={submitting}>
              {staged.file ? staged.file.name : "Choose file"}
              <input type="file" hidden accept={getAcceptString(type)} onChange={(e) => setStaged({ ...staged, file: e.target.files?.[0] })} />
            </Button>
            <Button variant="contained" color="primary" disabled={submitting || !staged.file} onClick={handleUpload}>
              Upload &amp; add
            </Button>
          </div>
        ) : (
          <Typography variant="caption" style={{ color: "#888" }}>
            Upload for “{type}” isn’t available here — select existing media instead.
          </Typography>
        )}

        {submitting && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12 }}>
            <CircularProgress size={18} /> <Typography variant="caption">Working…</Typography>
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function uploadableSimple(type) {
  return type === "image" || type === "audio" || type === "font" || type === "video";
}
